import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, Plus, Megaphone, MessageSquare, Loader2, Send, Trash2, Check,
  User, Camera, Pencil, ImageIcon, FileText, Eye, EyeOff, X, Heart,
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import {
  muralAnunciosService,
  MuralAnuncio,
  MuralAnuncioComment,
  MuralAnuncioPerson,
  MuralAnuncioPayload,
  MuralAnuncioLiker,
} from '../../../services/muralAnunciosService';
import { makeAbsolutePhotoUrl, fetchWithAuth } from '../../../services/api';
import { useKeycloak } from '../../../contexts/KeycloakContext';
import toast from 'react-hot-toast';
import PhotoActionModal from '../../PhotoActionModal';
import PhotoUploadModal, { PhotoResult } from '../../PhotoUploadModal';
import PhotoWebcamModal from '../../PhotoWebcamModal';
import { muralBodyBlocks, muralUnreferencedAttachments, muralListExcerpt } from './muralBody';

interface MuralAnunciosPanelProps {
  customerId: string;
  customerType: 'condominio' | 'empresa';
  onBack?: () => void;
  lightTheme?: boolean;
}

const MAX_PHOTOS = 5;

const ROLE_TYPE_OPTIONS_CONDO = [
  { value: 'resident', label: 'Morador' },
  { value: 'visitor', label: 'Visitante' },
  { value: 'service_provider', label: 'Prestador' },
];

const ROLE_TYPE_OPTIONS_EMPRESA = [
  { value: 'visitor', label: 'Visitante' },
  { value: 'service_provider', label: 'Prestador' },
  { value: 'staff', label: 'Funcionário' },
];

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function photosOf(a: MuralAnuncio): string[] {
  return [a.photo_url, a.photo_url_2, a.photo_url_3, a.photo_url_4, a.photo_url_5].filter((u): u is string => !!u);
}

function PersonAvatar({ person, fallbackName, size = 32 }: { person?: MuralAnuncioPerson; fallbackName?: string; size?: number }) {
  const photoUrl = person?.identity_photo_url || person?.profile_picture_url;
  const absoluteUrl = photoUrl ? makeAbsolutePhotoUrl(photoUrl) : null;
  const initials = person
    ? `${(person.first_name || '')[0] || ''}${(person.last_name || '')[0] || ''}`.toUpperCase()
    : (fallbackName || '').trim().split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();

  if (absoluteUrl) {
    return (
      <img
        src={absoluteUrl}
        alt={initials}
        className="rounded-full object-cover shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-amber-600 flex items-center justify-center shrink-0 text-white font-medium"
      style={{ width: size, height: size, fontSize: size * 0.35 }}
    >
      {initials || <User size={size * 0.5} />}
    </div>
  );
}

function authorName(a: MuralAnuncio): string {
  if (a.person) return `${a.person.first_name} ${a.person.last_name}`;
  return a.created_by_name || 'Administração';
}

const emptyForm = { title: '', body: '', visibleTo: [] as string[], commentsEnabled: false, commentableRoleTypes: [] as string[] };

/**
 * Mirrors the backend's `MuralTitleMinLength`. The title is what the board lists and what a
 * resident scans, so "ok", "teste" and "aviso" are not titles — on a board where every entry is
 * an aviso, "Aviso" names nothing.
 *
 * Counted in CHARACTERS on the trimmed title, not bytes — matching the backend, which counts
 * runes.
 */
export const MURAL_TITLE_MIN_LENGTH = 6;

export function muralTitleIsLongEnough(title: string): boolean {
  // Array.from, not a spread: this project targets es5, where spreading a string needs
  // `downlevelIteration`. Array.from is a plain call, so it type-checks at es5 with the es6 lib,
  // and it still splits by code point — an emoji counts once, like a rune on the backend.
  return Array.from(title.trim()).length >= MURAL_TITLE_MIN_LENGTH;
}

/** "182 KB" / "1.4 MB" — warns before somebody opens a 9MB scan. */
function formatPdfSize(bytes?: number | null): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MuralAnunciosPanel: React.FC<MuralAnunciosPanelProps> = ({ customerId, customerType, onBack, lightTheme = false }) => {
  const { hasAnyRole } = useKeycloak();
  const canManage = hasAnyRole('admin', 'supervisor', 'sindico', 'super_admin');
  // The síndico posts these, so the síndico can take them down. Being able to publish a notice
  // but not remove your own typo meant asking an admin.
  const canDelete = canManage;
  // Only a full admin reviews what was deleted — the row survives, so "o que foi removido?" has
  // an answer. A síndico sees the live board plus what is merely hidden.
  const canSeeDeleted = hasAnyRole('admin', 'super_admin');
  const roleTypeOptions = customerType === 'empresa' ? ROLE_TYPE_OPTIONS_EMPRESA : ROLE_TYPE_OPTIONS_CONDO;

  const [anuncios, setAnuncios] = useState<MuralAnuncio[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentsMode, setCommentsMode] = useState<'disabled' | 'optional'>('disabled');

  // Detail view
  const [selected, setSelected] = useState<MuralAnuncio | null>(null);
  // WHO curtiu — staff only; this panel IS the síndico's screen, so the list is shown inline.
  const [likers, setLikers] = useState<MuralAnuncioLiker[]>([]);
  const [likersOpen, setLikersOpen] = useState(false);
  const [likersLoading, setLikersLoading] = useState(false);
  const [comments, setComments] = useState<MuralAnuncioComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const commentsEndRef = useRef<HTMLDivElement>(null);

  // Create/edit form
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // The attached PDF. When set, the notice needs no body text: the document IS the announcement
  // (see requireMuralContent on the backend).
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState<string | null>(null);
  const [pdfSize, setPdfSize] = useState<number | null>(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const pdfInputRef = useRef<HTMLInputElement>(null);

  // Staff-only view of what is not on the board: hidden notices always (that is the point of
  // hiding rather than deleting) and, for admins, deleted ones on request.
  const [showRemoved, setShowRemoved] = useState(false);
  const [hidingId, setHidingId] = useState<string | null>(null);

  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPhotoActionModal, setShowPhotoActionModal] = useState(false);
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  const [showPhotoWebcamModal, setShowPhotoWebcamModal] = useState(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (!customerId) return;
    fetchWithAuth(`/api/v1/customers/${customerId}/settings`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          const mode = d.condo_data?.mural_anuncios_comments_mode || d.empresa_data?.mural_anuncios_comments_mode || 'disabled';
          setCommentsMode(mode === 'optional' ? 'optional' : 'disabled');
        }
      })
      .catch(() => {});
  }, [customerId]);

  const loadAnuncios = useCallback(async () => {
    setLoading(true);
    try {
      const data = await muralAnunciosService.listAnuncios(customerId, {
        // Staff always see hidden notices — a notice nobody can find again is a deletion with
        // extra steps.
        includeHidden: canManage,
        includeDeleted: canSeeDeleted && showRemoved,
      });
      setAnuncios(data);
    } catch {
      toast.error('Erro ao carregar anúncios');
    } finally {
      setLoading(false);
    }
  }, [customerId, canManage, canSeeDeleted, showRemoved]);

  useEffect(() => {
    loadAnuncios();
  }, [loadAnuncios]);

  const loadComments = useCallback(async (anuncioId: string) => {
    setLoadingComments(true);
    try {
      const data = await muralAnunciosService.listComments(customerId, anuncioId);
      setComments(data);
    } catch {
      toast.error('Erro ao carregar comentários');
    } finally {
      setLoadingComments(false);
    }
  }, [customerId]);

  const handleSelect = useCallback(async (a: MuralAnuncio) => {
    setSelected(a);
    setCommentText('');
    await loadComments(a.id);
  }, [loadComments]);

  useEffect(() => {
    if (commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [comments]);

  const resetForm = useCallback(() => {
    setForm(emptyForm);
    setPhotoPreviews([]);
    setPhotoUrls([]);
    setEditingId(null);
    setShowForm(false);
  }, []);

  const openCreateForm = () => {
    setForm(emptyForm);
    setPhotoPreviews([]);
    setPhotoUrls([]);
    setPdfUrl(null);
    setPdfName(null);
    setPdfSize(null);
    setEditingId(null);
    setShowForm(true);
  };

  const openEditForm = (a: MuralAnuncio) => {
    setForm({
      title: a.title,
      body: a.body,
      visibleTo: a.visible_to_role_types ?? [],
      commentsEnabled: a.comments_enabled,
      commentableRoleTypes: a.commentable_role_types ?? [],
    });
    setPhotoUrls(photosOf(a));
    setPhotoPreviews(photosOf(a).map(u => makeAbsolutePhotoUrl(u)));
    setPdfUrl(a.pdf_url ?? null);
    setPdfName(a.pdf_name ?? null);
    setPdfSize(a.pdf_size ?? null);
    setEditingId(a.id);
    setShowForm(true);
  };

  const handlePhotoResult = async (result: PhotoResult) => {
    if (photoPreviews.length >= MAX_PHOTOS) return;
    setPhotoPreviews(prev => [...prev, result.base64]);
    setUploadingPhoto(true);
    try {
      const file = new File([result.blob], 'anuncio.jpg', { type: 'image/jpeg' });
      const res = await muralAnunciosService.uploadPhoto(customerId, file);
      setPhotoUrls(prev => [...prev, res.photo_url]);
    } catch {
      toast.error('Erro ao enviar foto');
      setPhotoPreviews(prev => prev.slice(0, -1));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePdfPicked = async (file: File | undefined) => {
    if (!file) return;
    setUploadingPdf(true);
    try {
      const res = await muralAnunciosService.uploadPdf(customerId, file);
      setPdfUrl(res.pdf_url);
      setPdfName(res.pdf_name || file.name);
      setPdfSize(res.pdf_size || file.size);
    } catch (err: any) {
      // The backend says exactly what is wrong ("apenas arquivos PDF", "arquivo muito grande").
      toast.error(err?.message || 'Erro ao enviar o PDF');
    } finally {
      setUploadingPdf(false);
      if (pdfInputRef.current) pdfInputRef.current.value = '';
    }
  };

  const handleRemovePdf = () => {
    setPdfUrl(null);
    setPdfName(null);
    setPdfSize(null);
  };

  const handleToggleHidden = async (a: MuralAnuncio) => {
    const hide = !a.is_hidden;
    setHidingId(a.id);
    try {
      await muralAnunciosService.setHidden(customerId, a.id, hide);
      toast.success(hide ? 'Anúncio ocultado do mural' : 'Anúncio publicado novamente');
      if (selected?.id === a.id) setSelected({ ...selected, is_hidden: hide });
      loadAnuncios();
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao alterar a visibilidade');
    } finally {
      setHidingId(null);
    }
  };

  const handleRemovePhoto = (index: number) => {
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
    setPhotoUrls(prev => prev.filter((_, i) => i !== index));
  };

  const toggleRoleType = (list: string[], value: string): string[] =>
    list.includes(value) ? list.filter(v => v !== value) : [...list, value];

  const handleSubmit = async () => {
    if (!muralTitleIsLongEnough(form.title)) {
      toast.error(`O título deve ter pelo menos ${MURAL_TITLE_MIN_LENGTH} caracteres`);
      return;
    }
    // A PDF notice needs no body: the document is the announcement. What is refused is a notice
    // that would say nothing at all, which is the same rule the backend enforces.
    if (!form.body.trim() && !pdfUrl) {
      toast.error('Escreva o texto do aviso ou anexe um PDF');
      return;
    }
    if (uploadingPhoto || uploadingPdf) return;
    setSaving(true);
    try {
      const payload: MuralAnuncioPayload = {
        title: form.title.trim(),
        body: form.body.trim(),
        pdf_url: pdfUrl,
        pdf_name: pdfName,
        pdf_size: pdfSize,
        photo_url: photoUrls[0] || null,
        photo_url_2: photoUrls[1] || null,
        photo_url_3: photoUrls[2] || null,
        photo_url_4: photoUrls[3] || null,
        photo_url_5: photoUrls[4] || null,
        visible_to_role_types: form.visibleTo,
        comments_enabled: commentsMode === 'optional' ? form.commentsEnabled : false,
        commentable_role_types: form.commentableRoleTypes,
      };
      if (editingId) {
        const updated = await muralAnunciosService.updateAnuncio(customerId, editingId, payload);
        toast.success('Anúncio atualizado');
        resetForm();
        loadAnuncios();
        if (selected?.id === updated.id) setSelected(updated);
      } else {
        const created = await muralAnunciosService.createAnuncio(customerId, payload);
        toast.success('Anúncio publicado');
        resetForm();
        loadAnuncios();
        handleSelect(created);
      }
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao salvar anúncio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await muralAnunciosService.deleteAnuncio(customerId, id);
      toast.success('Anúncio removido');
      if (selected?.id === id) setSelected(null);
      loadAnuncios();
    } catch {
      toast.error('Erro ao remover anúncio');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSendComment = async () => {
    if (!selected || !commentText.trim()) return;
    setSendingComment(true);
    try {
      const comment = await muralAnunciosService.addComment(customerId, selected.id, { content: commentText.trim() });
      setComments(prev => [...prev, comment]);
      setCommentText('');
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao enviar comentário');
    } finally {
      setSendingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!selected) return;
    try {
      await muralAnunciosService.deleteComment(customerId, selected.id, commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch {
      toast.error('Erro ao remover comentário');
    }
  };

  // ─── Styles ───────────────────────────────────────────────────────────────
  const bg = lightTheme ? 'bg-white' : 'bg-gray-950';
  const border = lightTheme ? 'border-gray-200' : 'border-gray-800';
  const text = lightTheme ? 'text-gray-900' : 'text-gray-100';
  const subtext = lightTheme ? 'text-gray-500' : 'text-gray-400';
  const cardBg = lightTheme ? 'bg-gray-50 border-gray-200' : 'bg-gray-900 border-gray-700';
  const inputClass = cn(
    'w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500',
    lightTheme
      ? 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
      : 'bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500'
  );

  const roleTypeCheckboxes = (selectedValues: string[], onChange: (next: string[]) => void) => (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
      {roleTypeOptions.map(opt => (
        <label key={opt.value} className={cn('flex items-center gap-1.5 text-xs cursor-pointer', subtext)}>
          <input
            type="checkbox"
            checked={selectedValues.includes(opt.value)}
            onChange={() => onChange(toggleRoleType(selectedValues, opt.value))}
            className="rounded border-gray-400 text-amber-600 focus:ring-amber-500"
          />
          {opt.label}
        </label>
      ))}
    </div>
  );

  const formPanel = (
    <div className={cn('px-4 py-3 border-b shrink-0 space-y-2.5', border)}>
      <input
        className={inputClass}
        placeholder="Título do anúncio"
        value={form.title}
        onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
        maxLength={200}
        autoFocus
      />
      <textarea
        className={cn(inputClass, 'h-24 resize-none')}
        placeholder={pdfUrl ? 'Texto do anúncio (opcional — o PDF já é o aviso)' : 'Escreva o texto do anúncio...'}
        value={form.body}
        onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
      />

      {/* Attached PDF. The notice can BE the document: with one attached the body above becomes
          optional, which is the whole point — a síndico posting a convocação has nothing to
          retype. Rendered inline for residents, not offered as a download. */}
      <div>
        <input
          ref={pdfInputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={e => handlePdfPicked(e.target.files?.[0])}
        />
        {pdfUrl ? (
          <div className={cn('flex items-center gap-2 rounded-lg border px-3 py-2', border)}>
            <FileText size={16} className="text-amber-500 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className={cn('text-xs font-medium truncate', text)}>{pdfName || 'Documento.pdf'}</p>
              {formatPdfSize(pdfSize) && (
                <p className={cn('text-[11px]', subtext)}>{formatPdfSize(pdfSize)}</p>
              )}
            </div>
            <button
              type="button"
              onClick={handleRemovePdf}
              className={cn('p-1 rounded transition-colors', lightTheme ? 'hover:bg-red-50 text-gray-400 hover:text-red-600' : 'hover:bg-red-900/30 text-gray-500 hover:text-red-400')}
              title="Remover PDF"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => pdfInputRef.current?.click()}
            disabled={uploadingPdf}
            className={cn(
              'flex items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-xs transition-colors w-full',
              border, subtext,
              lightTheme ? 'hover:bg-gray-50' : 'hover:bg-gray-800/60'
            )}
          >
            {uploadingPdf ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
            {uploadingPdf ? 'Enviando PDF...' : 'Anexar PDF (o documento vira o aviso)'}
          </button>
        )}
      </div>

      {/* Photos */}
      <div className="flex flex-wrap gap-2">
        {photoPreviews.map((preview, i) => (
          <div key={i} className="relative rounded-lg overflow-hidden border border-gray-700/30 w-16 h-16 shrink-0">
            <img src={preview} alt={`foto ${i + 1}`} className="w-full h-full object-cover" />
            {uploadingPhoto && i === photoPreviews.length - 1 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <Loader2 className="h-4 w-4 animate-spin text-white" />
              </div>
            )}
            <button
              type="button"
              onClick={() => handleRemovePhoto(i)}
              disabled={uploadingPhoto}
              className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 hover:bg-black/80 rounded text-white transition-colors disabled:opacity-50"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
        {photoPreviews.length < MAX_PHOTOS && (
          <button
            type="button"
            onClick={() => setShowPhotoActionModal(true)}
            disabled={uploadingPhoto}
            className={cn(
              'w-16 h-16 shrink-0 flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-[10px] transition-colors disabled:opacity-50',
              lightTheme ? 'bg-gray-50 hover:bg-gray-100 border-gray-300 text-gray-500' : 'bg-gray-800 hover:bg-gray-700 border-gray-600 text-gray-400'
            )}
          >
            <Camera size={14} />
            Adicionar
          </button>
        )}
      </div>

      {/* Visibility audience */}
      <div>
        <p className={cn('text-xs font-medium mb-1.5', text)}>Visível para (deixe em branco para todos)</p>
        {roleTypeCheckboxes(form.visibleTo, next => setForm(f => ({ ...f, visibleTo: next })))}
      </div>

      {/* Comments config — only if customer allows optional comments */}
      {commentsMode === 'optional' && (
        <div className="space-y-1.5">
          <label className={cn('flex items-center gap-2 text-xs font-medium cursor-pointer', text)}>
            <input
              type="checkbox"
              checked={form.commentsEnabled}
              onChange={e => setForm(f => ({ ...f, commentsEnabled: e.target.checked }))}
              className="rounded border-gray-400 text-amber-600 focus:ring-amber-500"
            />
            Permitir comentários neste anúncio
          </label>
          {form.commentsEnabled && (
            <div className="ml-5">
              <p className={cn('text-xs mb-1.5', subtext)}>Quem pode comentar (deixe em branco para todos que veem o anúncio)</p>
              {roleTypeCheckboxes(form.commentableRoleTypes, next => setForm(f => ({ ...f, commentableRoleTypes: next })))}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          onClick={resetForm}
          className={cn('px-3 py-1.5 text-sm rounded-lg transition-colors', lightTheme ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-gray-300')}
        >
          Cancelar
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving || uploadingPhoto}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {editingId ? 'Salvar' : 'Publicar'}
        </button>
      </div>
    </div>
  );

  const photoModals = (
    <>
      <PhotoActionModal
        open={showPhotoActionModal}
        onClose={() => setShowPhotoActionModal(false)}
        onUpload={() => { setShowPhotoActionModal(false); setShowPhotoUploadModal(true); }}
        onWebcam={() => { setShowPhotoActionModal(false); setShowPhotoWebcamModal(true); }}
        onIPCamera={() => {}}
        title="Foto do Anúncio"
        darkMode={!lightTheme}
        hideIPCamera
      />
      <PhotoUploadModal
        open={showPhotoUploadModal}
        onClose={() => setShowPhotoUploadModal(false)}
        title="Foto do Anúncio"
        cropShape="rect"
        aspectRatio={4 / 3}
        darkMode={!lightTheme}
        disableFaceDetection
        onConfirm={async (result: PhotoResult) => { setShowPhotoUploadModal(false); await handlePhotoResult(result); }}
      />
      <PhotoWebcamModal
        open={showPhotoWebcamModal}
        onClose={() => setShowPhotoWebcamModal(false)}
        title="Foto do Anúncio"
        cropShape="rect"
        aspectRatio={4 / 3}
        darkMode={!lightTheme}
        disableFaceDetection
        onConfirm={async (result: PhotoResult) => { setShowPhotoWebcamModal(false); await handlePhotoResult(result); }}
      />
    </>
  );

  // ─── Detail View ──────────────────────────────────────────────────────────
  if (selected) {
    const photos = photosOf(selected);
    return (
      <div className={cn('flex flex-col h-full', bg)}>
        <div className={cn('flex items-center gap-3 px-4 py-3 border-b shrink-0', border)}>
          <button
            onClick={() => setSelected(null)}
            className={cn('p-1.5 rounded-lg transition-colors', lightTheme ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-gray-800 text-gray-400')}
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <p className={cn('font-semibold text-sm truncate', text)}>{selected.title}</p>
          </div>
          {canManage && (
            <button
              onClick={() => openEditForm(selected)}
              className={cn('p-1.5 rounded-lg transition-colors', lightTheme ? 'hover:bg-gray-100 text-gray-500' : 'hover:bg-gray-800 text-gray-400')}
            >
              <Pencil size={14} />
            </button>
          )}
          {canManage && (
            // Hiding is the softer, reversible act and sits BEFORE delete: the destructive
            // button must not be the first thing under a thumb heading for "take this down".
            <button
              onClick={() => handleToggleHidden(selected)}
              disabled={hidingId === selected.id}
              title={selected.is_hidden ? 'Publicar novamente' : 'Ocultar do mural'}
              className={cn('p-1.5 rounded-lg transition-colors', lightTheme ? 'hover:bg-gray-100 text-gray-500' : 'hover:bg-gray-800 text-gray-400')}
            >
              {hidingId === selected.id
                ? <Loader2 size={14} className="animate-spin" />
                : selected.is_hidden ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => handleDelete(selected.id)}
              disabled={deletingId === selected.id}
              className={cn('p-1.5 rounded-lg transition-colors', lightTheme ? 'hover:bg-red-50 text-gray-400 hover:text-red-600' : 'hover:bg-red-900/30 text-gray-500 hover:text-red-400')}
            >
              {deletingId === selected.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            </button>
          )}
        </div>

        <div className={cn('px-4 py-4 border-b shrink-0 max-h-64 overflow-y-auto', border)}>
          <div className="flex items-center gap-2 mb-2">
            <PersonAvatar person={selected.person} fallbackName={selected.created_by_name} size={28} />
            <span className={cn('text-sm font-medium', text)}>{authorName(selected)}</span>
            <span className={cn('text-xs ml-auto', subtext)}>{formatDate(selected.created_at)}</span>
          </div>
          {/* Curtidas. The count is what every resident sees; the names are this screen's
              privilege and nobody else's. */}
          <div className="flex items-center gap-3 mb-2">
            <span className={cn('inline-flex items-center gap-1.5 text-xs font-semibold', subtext)}>
              <Heart size={13} className="text-red-500" />
              {(selected.likes_count ?? 0) === 1 ? '1 curtida' : `${selected.likes_count ?? 0} curtidas`}
            </span>
            {(selected.likes_count ?? 0) > 0 && (
              <button
                type="button"
                onClick={async () => {
                  setLikersOpen(true);
                  setLikersLoading(true);
                  try {
                    setLikers(await muralAnunciosService.listLikers(customerId, selected.id));
                  } catch {
                    setLikers([]);
                  } finally {
                    setLikersLoading(false);
                  }
                }}
                className={cn('text-xs font-semibold', lightTheme ? 'text-blue-600 hover:underline' : 'text-blue-400 hover:underline')}
              >
                Ver quem curtiu
              </button>
            )}
          </div>

          {likersOpen && (
            <div className={cn('mb-3 rounded-lg border p-2.5', border)}>
              <div className="flex items-center justify-between mb-1.5">
                <p className={cn('text-[11px] font-semibold uppercase tracking-wider', subtext)}>Quem curtiu</p>
                <button
                  type="button"
                  onClick={() => setLikersOpen(false)}
                  className={cn('text-[11px]', subtext)}
                >
                  Fechar
                </button>
              </div>
              {likersLoading ? (
                <Loader2 size={14} className={cn('animate-spin', subtext)} />
              ) : likers.length === 0 ? (
                <p className={cn('text-xs', subtext)}>Ninguém curtiu este aviso ainda.</p>
              ) : (
                <ul className="space-y-1">
                  {likers.map(liker => (
                    <li key={liker.person_id} className={cn('text-xs flex items-center gap-1.5', text)}>
                      <Heart size={11} className="text-red-500 shrink-0" />
                      {liker.person_name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* The notice as a resident reads it in the app: the text split at its `[anexo N]`
              markers, each attachment where the síndico put it, and anything unplaced after.
              A notice with no markers — one written here, or before the apps gained the feature —
              has every attachment in that trailing group, which is exactly how this panel used
              to render all of them. See muralBody.ts. */}
          {(() => {
            const anexos: Array<{ kind: 'pdf' | 'photo'; url: string }> = [
              ...(selected.pdf_url ? [{ kind: 'pdf' as const, url: selected.pdf_url }] : []),
              ...photos.map(url => ({ kind: 'photo' as const, url })),
            ];
            const body = selected.body ?? '';
            const blocks = muralBodyBlocks(body, anexos.length);
            const leftovers = muralUnreferencedAttachments(body, anexos.length);

            const renderAnexo = (number: number, key: string) => {
              const anexo = anexos[number - 1];
              if (!anexo) return null;
              if (anexo.kind === 'pdf') {
                return (
                  <div className="mb-2" key={key}>
                    {/* Rendered, not linked: a mural PDF is normally a one-page notice, so the
                        document IS the announcement and burying it behind "abrir anexo" hides the
                        content. "Abrir" remains for a full-screen read. */}
                    <object
                      data={makeAbsolutePhotoUrl(anexo.url)}
                      type="application/pdf"
                      className={cn('w-full h-80 rounded-lg border', border)}
                    >
                      {/* Browsers with no inline PDF viewer land here rather than on a blank box. */}
                      <p className={cn('text-sm p-3', subtext)}>
                        Não foi possível exibir o PDF neste navegador.
                      </p>
                    </object>
                    <a
                      href={makeAbsolutePhotoUrl(anexo.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-1.5 text-xs text-amber-500 hover:underline"
                    >
                      <FileText size={12} />
                      {selected.pdf_name || 'Documento.pdf'}
                      {formatPdfSize(selected.pdf_size) && ` (${formatPdfSize(selected.pdf_size)})`}
                    </a>
                  </div>
                );
              }
              return (
                <img
                  key={key}
                  src={makeAbsolutePhotoUrl(anexo.url)}
                  alt={`anexo ${number}`}
                  className="w-full max-h-80 object-contain rounded-lg border border-gray-700/30 cursor-pointer mb-2"
                  onClick={() => window.open(makeAbsolutePhotoUrl(anexo.url), '_blank')}
                />
              );
            };

            return (
              <>
                {blocks.map((block, i) =>
                  block.kind === 'text' ? (
                    <p key={`t-${i}`} className={cn('text-sm whitespace-pre-wrap leading-relaxed mb-2', subtext)}>
                      {block.text}
                    </p>
                  ) : (
                    renderAnexo(block.number, `a-${i}`)
                  )
                )}
                {leftovers.map(number => renderAnexo(number, `l-${number}`))}
              </>
            );
          })()}
        </div>

        {/* Comments */}
        <div className="flex-1 overflow-y-auto custom-scrollbar px-4 py-3 space-y-3">
          {selected.comments_enabled ? (
            <>
              <p className={cn('text-xs font-medium uppercase tracking-wider', subtext)}>
                Comentários ({comments.length})
              </p>
              {loadingComments ? (
                <div className="flex justify-center py-8">
                  <Loader2 size={20} className={cn('animate-spin', subtext)} />
                </div>
              ) : comments.length === 0 ? (
                <p className={cn('text-sm text-center py-6', subtext)}>Nenhum comentário ainda.</p>
              ) : (
                comments.map(comment => (
                  <div key={comment.id} className="flex gap-2.5 group">
                    <PersonAvatar person={comment.person} size={28} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 mb-0.5">
                        <span className={cn('text-sm font-medium', text)}>
                          {comment.person ? `${comment.person.first_name} ${comment.person.last_name}` : 'Usuário'}
                        </span>
                        <span className={cn('text-xs', subtext)}>{formatDate(comment.created_at)}</span>
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          className={cn(
                            'ml-auto opacity-0 group-hover:opacity-100 p-1 rounded transition-all',
                            lightTheme ? 'hover:bg-red-50 text-gray-400 hover:text-red-600' : 'hover:bg-red-900/30 text-gray-600 hover:text-red-400'
                          )}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                      <p className={cn('text-sm whitespace-pre-wrap', subtext)}>{comment.content}</p>
                    </div>
                  </div>
                ))
              )}
              <div ref={commentsEndRef} />
            </>
          ) : (
            <p className={cn('text-sm text-center py-6', subtext)}>Comentários desabilitados para este anúncio</p>
          )}
        </div>

        {selected.comments_enabled && (
          <div className={cn('px-4 py-3 border-t shrink-0 flex gap-2', border)}>
            <input
              className={cn(inputClass, 'flex-1')}
              placeholder="Escreva um comentário..."
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendComment(); } }}
            />
            <button
              onClick={handleSendComment}
              disabled={sendingComment || !commentText.trim()}
              className="p-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white rounded-lg transition-colors"
            >
              {sendingComment ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        )}
        {photoModals}
      </div>
    );
  }

  // ─── List View ────────────────────────────────────────────────────────────
  return (
    <div className={cn('flex flex-col h-full', bg)}>
      <div className={cn('flex items-center gap-3 px-4 py-3 border-b shrink-0', border)}>
        {onBack && (
          <button
            onClick={onBack}
            className={cn('p-1.5 rounded-lg transition-colors', lightTheme ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-gray-800 text-gray-400')}
          >
            <ArrowLeft size={18} />
          </button>
        )}
        <Megaphone size={18} className={lightTheme ? 'text-amber-600' : 'text-amber-400'} />
        <h2 className={cn('font-semibold text-base', text)}>Mural de Anúncios</h2>
        <div className="flex-1" />
        {canSeeDeleted && !showForm && (
          // The row survives a deletion, so "o que foi removido do mural?" has an answer. Admins
          // only: a síndico sees the live board plus what is merely hidden.
          <button
            onClick={() => setShowRemoved(v => !v)}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border transition-colors',
              border,
              showRemoved ? 'text-amber-500 border-amber-500/40' : subtext
            )}
            title="Inclui anúncios removidos"
          >
            <Trash2 size={12} />
            {showRemoved ? 'Ocultando removidos' : 'Ver removidos'}
          </button>
        )}
        {canManage && !showForm && (
          <button
            onClick={openCreateForm}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm rounded-lg transition-colors"
          >
            <Plus size={14} />
            Novo
          </button>
        )}
      </div>

      {showForm && formPanel}

      <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-2">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className={cn('animate-spin', subtext)} />
          </div>
        ) : anuncios.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 space-y-3">
            <Megaphone size={40} className={subtext} />
            <p className={cn('text-sm', subtext)}>Nenhum anúncio publicado</p>
            {canManage && (
              <button onClick={openCreateForm} className="text-sm text-amber-500 hover:text-amber-400 transition-colors">
                Publicar anúncio
              </button>
            )}
          </div>
        ) : (
          anuncios.map(a => {
            const commentCount = a.comments?.length ?? 0;
            const photoCount = photosOf(a).length;
            return (
              <button
                key={a.id}
                onClick={() => handleSelect(a)}
                className={cn('w-full text-left rounded-xl border p-3 transition-colors hover:border-amber-500/50', cardBg)}
              >
                <div className="flex items-start gap-2">
                  <PersonAvatar person={a.person} fallbackName={a.created_by_name} size={28} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className={cn('text-sm font-medium truncate', text)}>{a.title}</span>
                      {/* Staff see notices residents do not. Unlabelled, they look live. */}
                      {!a.is_active && (
                        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-red-400 border border-red-400/40 rounded px-1.5">
                          Removido
                        </span>
                      )}
                      {a.is_active && a.is_hidden && (
                        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-amber-500 border border-amber-500/40 rounded px-1.5">
                          Oculto
                        </span>
                      )}
                    </div>
                    <p className={cn('text-xs truncate mb-1', subtext)}>
                      {/* Markers stripped: a row reading "[anexo 1] [anexo 2]" tells the operator
                          nothing about the aviso. See muralListExcerpt. */}
                      {muralListExcerpt(a, photosOf(a).length)}
                    </p>
                    <div className="flex items-center gap-3">
                      <span className={cn('text-xs', subtext)}>{authorName(a)}</span>
                      <span className={cn('text-xs', subtext)}>{formatDate(a.created_at)}</span>
                      {a.comments_enabled && commentCount > 0 && (
                        <span className={cn('flex items-center gap-1 text-xs', subtext)}>
                          <MessageSquare size={11} />
                          {commentCount}
                        </span>
                      )}
                      {photoCount > 0 && (
                        <span className={cn('flex items-center gap-1 text-xs', subtext)}>
                          <ImageIcon size={11} />
                          {photoCount}
                        </span>
                      )}
                      {a.pdf_url && (
                        <span className={cn('flex items-center gap-1 text-xs', subtext)}>
                          <FileText size={11} />
                          PDF
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
      {photoModals}
    </div>
  );
};

export default MuralAnunciosPanel;
